// ============================================
// PRIMARY HEALTH & PHYSICAL EDUCATION CURRICULUM - YEAR 4
// Based on Vanuatu National Syllabus
// ============================================

export const healthPEYear4 = {
  year: 4,
  subject: 'Health and Physical Education',
  totalWeeks: 40,
  periodsPerWeek: 4,
  terms: [
    {
      term: 'Term 1',
      weeks: 10,
      focus: 'Personal Health: Food and Food Preparation Hygiene',
      scheme: [
        {
          week: 1,
          topic: 'How Do I Know What is Food?',
          strand: 'Healthy Living',
          learningOutcomes: ['Identify different types of food', 'Distinguish between food and non-food items', 'Describe where food comes from'],
          activities: ['Food identification', 'Food vs non-food sorting', 'Food source exploration'],
          resources: ['Food pictures', 'Sorting cards', 'Food source charts']
        },
        {
          week: 2,
          topic: 'Is It Good for My Body?',
          strand: 'Healthy Living',
          learningOutcomes: ['Identify healthy and unhealthy foods', 'Describe why healthy food is important for the body', 'Classify foods into food groups'],
          activities: ['Healthy/unhealthy sorting', 'Body benefit discussion', 'Food group classification'],
          resources: ['Food group charts', 'Sorting materials', 'Body benefit posters']
        },
        {
          week: 3,
          topic: 'What Am I Eating?',
          strand: 'Healthy Living',
          learningOutcomes: ['Record what they eat in a day', 'Analyse their diet for balance', 'Identify foods they eat regularly and their food groups'],
          activities: ['Food diary keeping', 'Diet analysis', 'Food group matching'],
          resources: ['Food diary templates', 'Analysis worksheets', 'Food group guides']
        },
        {
          week: 4,
          topic: 'How Much Do I Need to Eat?',
          strand: 'Healthy Living',
          learningOutcomes: ['Understand portion sizes appropriate for their age', 'Describe the importance of eating the right amount', 'Plan a balanced meal'],
          activities: ['Portion size exploration', 'Balanced meal planning', 'Plate model activity'],
          resources: ['Portion guides', 'Meal planning templates', 'Plate model diagrams']
        },
        {
          week: 5,
          topic: 'Why Are Some Foods Not Good for Me?',
          strand: 'Healthy Living',
          learningOutcomes: ['Identify foods that are harmful in excess (sugar, salt, fat)', 'Describe health effects of unhealthy eating', 'Make healthier food choices'],
          activities: ['Harmful food identification', 'Health effects discussion', 'Healthy choice scenarios'],
          resources: ['Sugar/salt content cards', 'Health effect posters', 'Scenario worksheets']
        },
        {
          week: 6,
          topic: 'What Have I Learned About Food?',
          strand: 'Healthy Living',
          learningOutcomes: ['Summarise knowledge about food and nutrition', 'Create a healthy eating poster or guide', 'Share food knowledge with others'],
          activities: ['Knowledge review', 'Poster creation', 'Peer sharing'],
          resources: ['Poster materials', 'Review worksheets', 'Sharing guidelines']
        },
        {
          week: 7,
          topic: 'How Should I Be Handling Food?',
          strand: 'Healthy Living',
          learningOutcomes: ['Describe safe food handling practices', 'Identify the importance of hand washing before handling food', 'Demonstrate proper food handling techniques'],
          activities: ['Safe handling demonstration', 'Hand washing practice', 'Technique practice'],
          resources: ['Food handling guides', 'Hand washing posters', 'Practice materials']
        },
        {
          week: 8,
          topic: 'Where Should I Be Handling Food?',
          strand: 'Healthy Living',
          learningOutcomes: ['Identify clean and safe places for food preparation', 'Describe how to keep food preparation areas clean', 'Recognise hygiene risks in food preparation'],
          activities: ['Clean area identification', 'Cleaning practices', 'Risk identification'],
          resources: ['Hygiene checklists', 'Cleaning materials', 'Risk scenario cards']
        },
        {
          week: 9,
          topic: 'What Utensils Can I Use When Preparing Food?',
          strand: 'Healthy Living',
          learningOutcomes: ['Identify common kitchen utensils and their uses', 'Describe safe use of kitchen utensils', 'Demonstrate correct utensil use'],
          activities: ['Utensil identification', 'Safe use discussion', 'Utensil use demonstration'],
          resources: ['Kitchen utensils', 'Safety guides', 'Demonstration materials']
        },
        {
          week: 10,
          topic: 'Why and When Does Food Make Me Sick?',
          strand: 'Healthy Living',
          learningOutcomes: ['Identify causes of food-borne illness', 'Describe how food spoils and becomes unsafe', 'Explain ways to prevent food-related sickness'],
          activities: ['Illness cause identification', 'Food spoilage discussion', 'Prevention strategies'],
          resources: ['Food safety posters', 'Spoilage examples', 'Prevention guides']
        }
      ]
    },
    {
      term: 'Term 2',
      weeks: 10,
      focus: 'Growth and Development; Manipulative and Body Management Skills',
      scheme: [
        {
          week: 1,
          topic: 'What is Growth?',
          strand: 'Healthy Living',
          learningOutcomes: ['Define growth and development', 'Identify different types of growth', 'Describe how humans grow and change'],
          activities: ['Growth definition discussion', 'Growth types identification', 'Growth timeline creation'],
          resources: ['Growth charts', 'Timeline materials', 'Discussion guides']
        },
        {
          week: 2,
          topic: 'What is Physical Growth?',
          strand: 'Healthy Living',
          learningOutcomes: ['Describe physical growth changes', 'Measure and record physical growth indicators', 'Explain factors that support physical growth'],
          activities: ['Physical growth observation', 'Measurement activities', 'Factor identification'],
          resources: ['Measuring tools', 'Growth charts', 'Factor cards']
        },
        {
          week: 3,
          topic: 'What is Mental Growth?',
          strand: 'Healthy Living',
          learningOutcomes: ['Describe mental growth and learning', 'Identify ways the brain develops', 'Explain activities that support mental growth'],
          activities: ['Mental growth discussion', 'Brain development exploration', 'Learning activity identification'],
          resources: ['Brain diagrams', 'Learning activity cards', 'Discussion guides']
        },
        {
          week: 4,
          topic: 'What is Emotional Growth?',
          strand: 'Healthy Living',
          learningOutcomes: ['Identify different emotions', 'Describe how emotional responses change with age', 'Demonstrate healthy ways to manage emotions'],
          activities: ['Emotion identification', 'Emotional change discussion', 'Emotion management practice'],
          resources: ['Emotion cards', 'Scenario worksheets', 'Management strategy guides']
        },
        {
          week: 5,
          topic: 'What is Social Growth?',
          strand: 'Healthy Living',
          learningOutcomes: ['Describe social growth and relationships', 'Identify social skills needed for getting along with others', 'Demonstrate positive social interactions'],
          activities: ['Social skills identification', 'Relationship discussion', 'Social interaction practice'],
          resources: ['Social skills cards', 'Role play scenarios', 'Discussion guides']
        },
        {
          week: 6,
          topic: 'Throwing and Catching',
          strand: 'Physical Education',
          learningOutcomes: ['Demonstrate correct throwing technique', 'Catch objects with two hands consistently', 'Apply throwing and catching in games'],
          activities: ['Throwing practice', 'Catching drills', 'Throwing/catching games'],
          resources: ['Balls of various sizes', 'Targets', 'Game equipment']
        },
        {
          week: 7,
          topic: 'Kicking and Dribbling',
          strand: 'Physical Education',
          learningOutcomes: ['Demonstrate correct kicking technique', 'Dribble a ball with control', 'Apply kicking and dribbling in small games'],
          activities: ['Kicking practice', 'Dribbling drills', 'Small-sided games'],
          resources: ['Footballs', 'Cones', 'Goal markers']
        },
        {
          week: 8,
          topic: 'Passing and Rolling',
          strand: 'Physical Education',
          learningOutcomes: ['Pass a ball accurately to a partner', 'Roll a ball with control towards a target', 'Work cooperatively in passing games'],
          activities: ['Partner passing', 'Target rolling', 'Cooperative games'],
          resources: ['Balls', 'Targets', 'Cones']
        },
        {
          week: 9,
          topic: 'Dribbling and Passing Combined',
          strand: 'Physical Education',
          learningOutcomes: ['Combine dribbling and passing in movement', 'Demonstrate ball control while moving', 'Apply skills in modified team games'],
          activities: ['Dribble-pass combinations', 'Moving ball control', 'Modified games'],
          resources: ['Balls', 'Cones', 'Bibs/markers']
        },
        {
          week: 10,
          topic: 'Striking with a Bat/Hand',
          strand: 'Physical Education',
          learningOutcomes: ['Strike a ball using hand or bat', 'Demonstrate correct striking stance and technique', 'Apply striking skills in modified games'],
          activities: ['Hand striking practice', 'Bat striking practice', 'Striking games'],
          resources: ['Bats', 'Balls', 'Tees/cones']
        }
      ]
    },
    {
      term: 'Term 3',
      weeks: 10,
      focus: 'Manipulative Skills in Competitive Games and Team Sports',
      scheme: [
        {
          week: 1,
          topic: 'Volleying',
          strand: 'Physical Education',
          learningOutcomes: ['Demonstrate correct volleying technique', 'Volley a ball over a net or to a partner', 'Apply volleying in modified games'],
          activities: ['Volleying practice', 'Partner volley', 'Modified volleyball'],
          resources: ['Volleyballs', 'Nets', 'Court markers']
        },
        {
          week: 2,
          topic: 'Football/Futsal/Beach Soccer',
          strand: 'Physical Education',
          learningOutcomes: ['Apply dribbling, passing and shooting in football', 'Understand basic rules of football/futsal', 'Play cooperatively in team games'],
          activities: ['Skills practice', 'Rules learning', 'Modified football games'],
          resources: ['Footballs', 'Goals', 'Cones/markers']
        },
        {
          week: 3,
          topic: 'Tough Rugby/Handball',
          strand: 'Physical Education',
          learningOutcomes: ['Demonstrate passing and catching in rugby/handball', 'Understand basic rules of touch rugby or handball', 'Play safely in contact-modified games'],
          activities: ['Passing drills', 'Rules introduction', 'Modified games'],
          resources: ['Rugby balls', 'Handball equipment', 'Markers']
        },
        {
          week: 4,
          topic: 'Volleyball/Beach Volleyball/Foot Volley',
          strand: 'Physical Education',
          learningOutcomes: ['Apply serving and volleying skills', 'Understand rotation and scoring rules', 'Participate in modified volleyball games'],
          activities: ['Serving practice', 'Rotation practice', 'Modified games'],
          resources: ['Volleyballs', 'Nets', 'Court markers']
        },
        {
          week: 5,
          topic: 'Cricket/Baseball',
          strand: 'Physical Education',
          learningOutcomes: ['Demonstrate batting, bowling/pitching and fielding', 'Understand basic rules of cricket or baseball', 'Play cooperatively in modified games'],
          activities: ['Batting practice', 'Bowling/pitching drills', 'Modified games'],
          resources: ['Bats', 'Balls', 'Wickets/bases']
        },
        {
          week: 6,
          topic: 'Team Sports Tournament',
          strand: 'Physical Education',
          learningOutcomes: ['Apply skills learned across multiple sports', 'Demonstrate sportsmanship and fair play', 'Work as a team in competitive situations'],
          activities: ['Multi-sport rotation', 'Tournament play', 'Sportsmanship discussion'],
          resources: ['Various sports equipment', 'Score sheets', 'Bibs']
        },
        {
          week: 7,
          topic: 'Fitness and Body Management',
          strand: 'Physical Education',
          learningOutcomes: ['Demonstrate basic fitness exercises', 'Understand the importance of regular physical activity', 'Measure and record fitness levels'],
          activities: ['Fitness circuit', 'Activity importance discussion', 'Fitness testing'],
          resources: ['Fitness equipment', 'Recording sheets', 'Timer']
        },
        {
          week: 8,
          topic: 'Balance and Coordination Activities',
          strand: 'Physical Education',
          learningOutcomes: ['Demonstrate balance in static and dynamic activities', 'Show coordination in movement sequences', 'Apply balance and coordination in games'],
          activities: ['Balance challenges', 'Coordination circuits', 'Balance games'],
          resources: ['Balance beams', 'Cones', 'Obstacle course materials']
        },
        {
          week: 9,
          topic: 'Revision - Sports Skills Assessment',
          strand: 'Physical Education',
          learningOutcomes: ['Demonstrate proficiency in key sports skills', 'Show improvement from beginning of year', 'Reflect on personal fitness journey'],
          activities: ['Skills assessment', 'Progress reflection', 'Peer evaluation'],
          resources: ['Assessment rubrics', 'Sports equipment', 'Reflection sheets']
        },
        {
          week: 10,
          topic: 'Term 3 Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Demonstrate competence in team sports', 'Show sportsmanship and fair play', 'Reflect on health and physical education learning'],
          activities: ['Practical assessment', 'Written quiz', 'Portfolio review'],
          resources: ['Assessment materials', 'Quiz papers', 'Portfolio folders']
        }
      ]
    },
    {
      term: 'Term 4',
      weeks: 10,
      focus: 'Group Performance, Dance Aerobic Activities, Traditional Games and Field Events',
      scheme: [
        {
          week: 1,
          topic: 'Group Performance - Introduction',
          strand: 'Physical Education',
          learningOutcomes: ['Understand what group performance involves', 'Identify elements of a good group performance', 'Begin planning a group performance'],
          activities: ['Group performance introduction', 'Element identification', 'Group formation and planning'],
          resources: ['Performance examples', 'Planning templates', 'Group work guides']
        },
        {
          week: 2,
          topic: 'Dance and Aerobic Activities - Warm Up Routines',
          strand: 'Physical Education',
          learningOutcomes: ['Learn and perform warm-up dance routines', 'Understand the importance of warming up', 'Follow rhythmic movement patterns'],
          activities: ['Warm-up routines', 'Dance movement practice', 'Rhythm activities'],
          resources: ['Music player', 'Dance space', 'Routine cards']
        },
        {
          week: 3,
          topic: 'Dance and Aerobic Activities - Choreography',
          strand: 'Physical Education',
          learningOutcomes: ['Create simple dance sequences', 'Combine movements to music', 'Perform choreographed routines in groups'],
          activities: ['Sequence creation', 'Music-movement combination', 'Group rehearsal'],
          resources: ['Music player', 'Choreography guides', 'Performance space']
        },
        {
          week: 4,
          topic: 'Group Performance and Dance Presentation',
          strand: 'Physical Education',
          learningOutcomes: ['Rehearse and refine group performance', 'Present performance to an audience', 'Give and receive constructive feedback'],
          activities: ['Final rehearsal', 'Performance presentation', 'Feedback session'],
          resources: ['Performance space', 'Music', 'Feedback forms']
        },
        {
          week: 5,
          topic: 'Group Performance - Dance Aerobic Assessment',
          strand: 'Physical Education',
          learningOutcomes: ['Demonstrate dance and aerobic skills', 'Perform confidently in front of others', 'Evaluate own and peer performance'],
          activities: ['Performance assessment', 'Peer evaluation', 'Self-reflection'],
          resources: ['Assessment rubrics', 'Music', 'Reflection worksheets']
        },
        {
          week: 6,
          topic: 'Traditional Games of Vanuatu',
          strand: 'Physical Education',
          learningOutcomes: ['Identify traditional games from different islands', 'Learn the rules and skills of traditional games', 'Play traditional games with proper technique'],
          activities: ['Traditional game introduction', 'Rules learning', 'Game play'],
          resources: ['Traditional game equipment', 'Rules guides', 'Playing field']
        },
        {
          week: 7,
          topic: 'Traditional Games from Other Pacific Islands',
          strand: 'Physical Education',
          learningOutcomes: ['Identify traditional games from neighbouring Pacific islands', 'Compare games across Pacific cultures', 'Play a variety of Pacific traditional games'],
          activities: ['Pacific games exploration', 'Cultural comparison', 'Game play'],
          resources: ['Game equipment', 'Pacific games guides', 'Comparison charts']
        },
        {
          week: 8,
          topic: 'Customised Field Events - Running and Jumping',
          strand: 'Physical Education',
          learningOutcomes: ['Demonstrate correct running and jumping techniques', 'Participate in modified field events', 'Measure and record personal performances'],
          activities: ['Running technique practice', 'Jumping events', 'Performance recording'],
          resources: ['Measuring tape', 'Cones/markers', 'Recording sheets']
        },
        {
          week: 9,
          topic: 'Customised Field Events - Throwing',
          strand: 'Physical Education',
          learningOutcomes: ['Demonstrate correct throwing technique for field events', 'Participate in modified throwing events', 'Show sportsmanship in competitive events'],
          activities: ['Throwing technique practice', 'Modified shot put/javelin', 'Mini athletics carnival'],
          resources: ['Safe throwing equipment', 'Measuring tools', 'Event cards']
        },
        {
          week: 10,
          topic: 'End-of-Year Sports Day and Assessment',
          strand: 'All Strands',
          learningOutcomes: ['Participate in end-of-year sports carnival', 'Demonstrate skills across traditional and modern sports', 'Reflect on health and PE learning throughout the year'],
          activities: ['Sports carnival', 'Multi-event participation', 'Year reflection'],
          resources: ['Sports equipment', 'Carnival materials', 'Reflection worksheets']
        }
      ]
    }
  ]
};

export default healthPEYear4;
